/**
 * Utility functions for checking tier-specific feature availability
 * Features are configured in Firebase: system_config/tier_features
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

export interface TierFeatures {
  // === Core features ===
  analytics: boolean;
  approvalSystem: boolean;
  landingPageBuilder: boolean;
  allQuestionTypes: boolean; // If false, only basic question types available
  customBranding: boolean;
  prioritySupport: boolean;
  multiLevelApproval: boolean;
  advancedAnalytics: boolean;
  pdfExport: boolean;
  excelExport: boolean;
  mobileAdminPanel: boolean;
  mobileFormFilling: boolean;

  // === Platform features (display — available on all/most tiers) ===
  formThemes: boolean;
  publicFormSharing: boolean;
  mobileApp: boolean;
  offlineMode: boolean;
  signatures: boolean;
  imageCapture: boolean;
  imageAnnotations: boolean;
  emailNotifications: boolean;

  // === Functional features (gated by tier) ===
  conditionalLogic: boolean;
  editSubmissions: boolean;
  reminders: boolean;
  calendarView: boolean;
  customSlug: boolean;
  companyAccount: boolean;
  passwordProtectedPages: boolean;
  questionTemplates: boolean;
  distributionGroups: boolean;
}

export interface TierFeaturesConfig {
  free: TierFeatures;
  pro: TierFeatures;
  proPlus: TierFeatures;
  enterprise: TierFeatures;
  lastUpdated?: string;
  lastUpdatedBy?: string;
}

// Default feature flags (fallback if config doesn't exist)
// Display-only features (formThemes, signatures, etc.) are true for all tiers so pricing UI shows them.
// customBranding = company logo on forms (all tiers); PDF watermark/unbranded PDF is tier-specific via pdfExport/allowPdfCopy.
// advancedAnalytics = reserved for future cross-form analytics; today "Analytics" = Response Dashboard + PDF/Excel export only.
const DEFAULT_TIER_FEATURES: TierFeaturesConfig = {
  free: {
    // Core
    analytics: false,
    approvalSystem: false,
    landingPageBuilder: false,
    allQuestionTypes: false,
    customBranding: true, // All tiers can add company logo; PDF branding is tier-specific
    prioritySupport: true, // Display: shown for all
    multiLevelApproval: true, // Display: shown for all
    advancedAnalytics: false, // Future cross-form analytics; today same as analytics
    pdfExport: false, // Free: no exports (strategy: Excluded)
    excelExport: false,
    mobileAdminPanel: true, // Display: shown for all
    mobileFormFilling: true,
    // Platform (all true — available to all tiers)
    formThemes: true,
    publicFormSharing: true,
    mobileApp: true,
    offlineMode: true,
    signatures: true,
    imageCapture: true,
    imageAnnotations: true,
    emailNotifications: true,
    // Functional
    conditionalLogic: false,
    editSubmissions: false,
    reminders: false,
    calendarView: false,
    customSlug: false,
    companyAccount: false,
    passwordProtectedPages: false,
    questionTemplates: false,
    distributionGroups: false,
  },
  pro: {
    // Core
    analytics: true,
    approvalSystem: false,
    landingPageBuilder: false,
    allQuestionTypes: false,
    customBranding: true,
    prioritySupport: true,
    multiLevelApproval: true,
    advancedAnalytics: false, // Cross-form = Enterprise only
    pdfExport: true,
    excelExport: true,
    mobileAdminPanel: true,
    mobileFormFilling: true,
    formThemes: true,
    publicFormSharing: true,
    mobileApp: true,
    offlineMode: true,
    signatures: true,
    imageCapture: true,
    imageAnnotations: true,
    emailNotifications: true,
    conditionalLogic: false,
    editSubmissions: true,
    reminders: true,
    calendarView: false,
    customSlug: false,
    companyAccount: false,
    passwordProtectedPages: false,
    questionTemplates: true,
    distributionGroups: true,
  },
  proPlus: {
    // Core
    analytics: true,
    approvalSystem: true,
    landingPageBuilder: false,
    allQuestionTypes: true,
    customBranding: true,
    prioritySupport: true,
    multiLevelApproval: false, // Single-approver only; multi-level = Enterprise
    advancedAnalytics: false, // Cross-form = Enterprise only
    pdfExport: true,
    excelExport: true,
    mobileAdminPanel: true,
    mobileFormFilling: true,
    formThemes: true,
    publicFormSharing: true,
    mobileApp: true,
    offlineMode: true,
    signatures: true,
    imageCapture: true,
    imageAnnotations: true,
    emailNotifications: true,
    conditionalLogic: true,
    editSubmissions: true,
    reminders: true,
    calendarView: true,
    customSlug: false,
    companyAccount: false,
    passwordProtectedPages: false,
    questionTemplates: true,
    distributionGroups: true,
  },
  enterprise: {
    analytics: true,
    approvalSystem: true,
    landingPageBuilder: true,
    allQuestionTypes: true,
    customBranding: true,
    prioritySupport: true,
    multiLevelApproval: true,
    advancedAnalytics: true,
    pdfExport: true,
    excelExport: true,
    mobileAdminPanel: true,
    mobileFormFilling: true,
    formThemes: true,
    publicFormSharing: true,
    mobileApp: true,
    offlineMode: true,
    signatures: true,
    imageCapture: true,
    imageAnnotations: true,
    emailNotifications: true,
    conditionalLogic: true,
    editSubmissions: true,
    reminders: true,
    calendarView: true,
    customSlug: true,
    companyAccount: true,
    passwordProtectedPages: true,
    questionTemplates: true,
    distributionGroups: true,
  },
};

let cachedFeatures: TierFeaturesConfig | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get feature flags for a specific tier
 */
export const getTierFeatures = async (tier: 'free' | 'pro' | 'proPlus' | 'enterprise'): Promise<TierFeatures> => {
  // Check cache first
  const now = Date.now();
  if (cachedFeatures && (now - cacheTimestamp) < CACHE_DURATION) {
    // Merge with defaults to ensure new flags fall back correctly
    return { ...DEFAULT_TIER_FEATURES[tier], ...(cachedFeatures[tier] || {}) };
  }

  try {
    const configDoc = await getDoc(doc(db, 'system_config', 'tier_features'));

    if (configDoc.exists()) {
      const data = configDoc.data() as TierFeaturesConfig;
      cachedFeatures = data;
      cacheTimestamp = now;
      // Merge with defaults so new flags not yet in Firebase get correct defaults
      return { ...DEFAULT_TIER_FEATURES[tier], ...(data[tier] || {}) };
    } else {
      // Config doesn't exist, use defaults
      cachedFeatures = DEFAULT_TIER_FEATURES;
      cacheTimestamp = now;
      return DEFAULT_TIER_FEATURES[tier];
    }
  } catch (error) {
    console.error('Error loading tier features config:', error);
    // Return defaults on error
    return DEFAULT_TIER_FEATURES[tier];
  }
};

/**
 * Get all tier features config (for admin use)
 */
export const getAllTierFeatures = async (): Promise<TierFeaturesConfig> => {
  // Check cache first
  const now = Date.now();
  if (cachedFeatures && (now - cacheTimestamp) < CACHE_DURATION) {
    // Merge each tier with defaults for backward compatibility
    return {
      free: { ...DEFAULT_TIER_FEATURES.free, ...(cachedFeatures.free || {}) },
      pro: { ...DEFAULT_TIER_FEATURES.pro, ...(cachedFeatures.pro || {}) },
      proPlus: { ...DEFAULT_TIER_FEATURES.proPlus, ...(cachedFeatures.proPlus || {}) },
      enterprise: { ...DEFAULT_TIER_FEATURES.enterprise, ...(cachedFeatures.enterprise || {}) },
      lastUpdated: cachedFeatures.lastUpdated,
      lastUpdatedBy: cachedFeatures.lastUpdatedBy,
    };
  }

  try {
    const configDoc = await getDoc(doc(db, 'system_config', 'tier_features'));

    if (configDoc.exists()) {
      const data = configDoc.data() as TierFeaturesConfig;
      cachedFeatures = data;
      cacheTimestamp = now;
      return {
        free: { ...DEFAULT_TIER_FEATURES.free, ...(data.free || {}) },
        pro: { ...DEFAULT_TIER_FEATURES.pro, ...(data.pro || {}) },
        proPlus: { ...DEFAULT_TIER_FEATURES.proPlus, ...(data.proPlus || {}) },
        enterprise: { ...DEFAULT_TIER_FEATURES.enterprise, ...(data.enterprise || {}) },
        lastUpdated: data.lastUpdated,
        lastUpdatedBy: data.lastUpdatedBy,
      };
    } else {
      cachedFeatures = DEFAULT_TIER_FEATURES;
      cacheTimestamp = now;
      return DEFAULT_TIER_FEATURES;
    }
  } catch (error) {
    console.error('Error loading tier features config:', error);
    return DEFAULT_TIER_FEATURES;
  }
};

/**
 * Clear the cache (call after updating tier features)
 */
export const clearTierFeaturesCache = () => {
  cachedFeatures = null;
  cacheTimestamp = 0;
};

/**
 * Get default tier features config (for reference)
 */
export const getDefaultTierFeatures = (): TierFeaturesConfig => {
  return DEFAULT_TIER_FEATURES;
};
